import {
  FluidAnchorPosition,
  FluidElementState,
  FluidInPageNavConfig,
  FluidKeyedObject,
  FluidMenuItem,
  FluidSidebarConfig,
  FluidTheme,
  FluidThemeInterface,
  FluidThemePalette,
  FluidThemeVariant,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { Subscription } from 'rxjs';

import {
  CONTEXT_BAR_ENTRY,
  FOOTER_ENTRY,
  HEADER_ENTRY,
  IN_PAGE_NAV_INCOMPATIBLE_SIDEBARS,
  IN_PAGE_NAV_INVALID_ANCHOR,
  INVALID_FOOTER_IMPLEMENTATION,
  INVALID_FOOTER_SLOT,
} from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { ElementRegistry } from '../../utils/elements/element-registry.utils';
import { Utils } from '../../utils/utils';
import {
  PageContextBarSlot,
  PageFooterSlot,
  PageNav,
  PageSidebar,
} from './components/fluid-page.components';

/**
 * @displayName Page
 *
 * @contextData FLUID Page is a foundational component that provides the root architecture for all page content. It's also responsible for Sidebar implementation, and manages the layout structure of page content in relation to the Sidebar(s).
 * <br/><br/>
 * <strong>FLUID Page should be implemented ONCE per page at the ROOT of the page body</strong> (typically as a sibling of FLUID Header & FLUID Footer), <strong>& should NOT be used for micro-UIs (unless the micro-UI is used as the page's root)</strong>.
 *
 * @slot - Content displayed within the page body
 * @slot left-sidebar-content - Named slot for left Sidebar content (only available if "leftSidebarConfig" Prop defined)
 * @slot right-sidebar-content - Named slot for right Sidebar content (only available if "rightSidebarConfig" Prop defined)
 * @slot page-context-bar - Named slot for Page Context Bar component (only necessary if Page Context Bar is not a direct child of FLUID Page)
 * @slot page-footer - Named slot for "sticky" content along the bottom of the viewport (DO NOT implement FLUID Footer in this slot)
 */
@Component({
  tag: 'fluid-page',
  styleUrl: 'fluid-page.scss',
  shadow: true,
})
export class FluidPage implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * RxJS subscription to global Element Registry. Allows Page to be
   * aware of other "sticky" components' positions within the DOM.
   * Stored as a variable to allow unsubscribing when component is
   * disconnected/removed from DOM.
   */
  private _registrySubscription$: Subscription;

  /**
   * Reference to the MutationObserver that monitors Context Bar for
   * changes in slotted child content.
   */
  private _slotObserver: MutationObserver;

  /**
   * Internal copy of "inPageNavConfig" used for rendering. Allows "anchor"
   * property to be modified based on Sidebar configuration without mutating
   * the original Prop.
   */
  private _inPageNavConfig: FluidInPageNavConfig;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidPageElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */

  @Prop({ mutable: true }) fluidTranslate: {
    pageTitle?: string;
  };
  private _translateId: string;
  @State() private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * Boolean that indicates whether child element(s) exist(s) with
   * "page-context-bar" slot.
   */
  @State() _hasContextBarSlot = false;

  /**
   * Boolean that indicates whether child element(s) exist(s) with
   * "page-footer" slot.
   */
  @State() _hasFooterSlot = false;

  /**
   * Sum total of FLUID Header + FLUID Footer component heights. Used
   * for calculating "min-height" of FLUID Page.
   */
  @State() _pageHeightOffset = 0;

  /**
   * Page Context Bar's pixel offset from top of page when FLUID Header
   * is "sticky". If Header is NOT "sticky", set to 0.
   */
  @State() _contextBarOffset = 0;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * When provided, adjusts the current theme's color palette & appearance.
   * NOTE: a theme palette acts as a "style override" applied ON TOP of
   * the current theme & isn't necessary for "standard" theme aesthetics.
   */
  @Prop({ mutable: true }) themePalette: FluidThemePalette;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * When provided, displays a title above slotted FLUID Page content.
   */
  @Prop({ mutable: true }) pageTitle: string;

  /**
   * Left sidebar config object: setting this will cause a left Sidebar
   * to be rendered, regardless if an array of FluidMenuItems is included.
   * Leaving it unset (default) means the left Sidebar will NOT be rendered.
   */
  @Prop({ mutable: true }) leftSidebarConfig: FluidSidebarConfig;

  /**
   * Right sidebar config object: setting this will cause a right Sidebar
   * to be rendered, regardless if an array of FluidMenuItems is included.
   * Leaving it unset (default) means the right Sidebar will NOT be rendered.
   */
  @Prop({ mutable: true }) rightSidebarConfig: FluidSidebarConfig;

  /**
   * When provided, FLUID Page will render In-Page Navigation (aka "table
   * of contents") on one side of the page.
   */
  @Prop({ mutable: true }) inPageNavConfig: FluidInPageNavConfig;

  @Watch('inPageNavConfig')
  onNavConfigChange() {
    this.initializeInPageNavConfig();
  }

  /**
   * Sets page background color depending on variation chosen. NOTE: currently
   * only supports "atmospheric white" background color.
   */
  @Prop({ mutable: true }) themeVariant: FluidThemeVariant =
    FluidThemeVariant.DEFAULT;

  /**
   * When true, adds a subtle drop shadow to the "page-footer" slot. NOTE:
   * "page-footer" slot MUST be used for this to take effect.
   */
  @Prop({ mutable: true }) pageFooterDropShadow = false;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    useI18n.bind(this)();
    Utils.setGlobalTheme(this);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action detectSlotContent - finds / watches for slotted Page Context Bar
   * @action hasSidebarDeprecationWarning - "hasSidebar" Prop deprecation warning (if used)
   * @action sidebarLinksDeprecationWarning - "sidebarLinks" Prop deprecation warning (if used)
   * @action sidebarOpenDeprecationWarning - "sidebarOpen" Prop deprecation warning (if used)
   * @action mapLocalToConfig - map deprecated Props to leftSidebarConfig Prop
   * @action subscribeToElementRegistry - initiates subscription to Element Registry
   */
  componentWillLoad(): void {
    this.detectSlotContent();
    this.initializeInPageNavConfig();
    this.hasSidebarDeprecationWarning();
    this.sidebarLinksDeprecationWarning();
    this.sidebarOpenDeprecationWarning();
    this.mapLocalToConfig();
    this.subscribeToElementRegistry();
  }

  /**
   * Called when component is about to be updated because some Prop( {mutable: true }) or State() changed.
   * @action mapLocalToConfig - map deprecated Props to leftSidebarConfig Prop
   */
  componentWillUpdate(): void {
    this.mapLocalToConfig();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action unsubscribe - unsubscribes from Element Registry BehaviorSubject
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
    this._registrySubscription$?.unsubscribe();
    delete this._registrySubscription$;
    this._slotObserver?.disconnect();
    this._slotObserver = undefined;
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * Listener for "menuItemClick" Events that re-emits the deprecated
   * "sidebarLinkClicked" Event for ongoing support.
   */
  @Listen('menuItemClick')
  onMenuItemClick(event: CustomEvent<FluidMenuItem>) {
    this.sidebarLinkClicked.emit(event.detail);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Determines if any direct children of Page have "page-context-bar"
   * slot attr, then initializes MutationObserver that monitors root element
   * for changes in slotted content. If Page Context Bar component exists
   * as a child of Page, automatically applies "page-context-bar" slot
   * attribute (i.e. allows Context Bar to be used without "slot" attr).
   * If slotted Context Bar (or element with correct "slot" attr) exists,
   * Page will dynamically render a "sticky" wrapper & named slot element
   * for Context Bar.
   */
  detectSlotContent(): void {
    const contextBarSlot = 'page-context-bar';

    // If Page Context Bar provided to FLUID Page as a direct child, dynamically add "slot" attr
    this.host
      ?.querySelector(':scope > fluid-page-context-bar')
      ?.setAttribute('slot', contextBarSlot);

    this._hasContextBarSlot = Utils.hasSlotContent(this.host, contextBarSlot);
    this._hasFooterSlot = Utils.hasSlotContent(this.host, 'page-footer');

    this.validateFooter();

    if (isNullOrUndefined(this._slotObserver)) {
      this._slotObserver = Utils.watchChildContent(
        this.host,
        Utils.debounce(() => this.detectSlotContent(), 100),
        { attributes: true, attributeFilter: ['slot'], childList: true }
      );
    }
  }

  /**
   * Validates that FLUID Footer is NOT implemented within FLUID Page -
   * logs console warning if it is. If Footer is used within "page-footer"
   * slot, removes slot.
   */
  validateFooter() {
    const footerRef = this.host.querySelector('fluid-footer');
    if (!footerRef) return;
    Utils.consoleWarn(this.host, INVALID_FOOTER_IMPLEMENTATION);

    if (Utils.closestAncestor(footerRef, '[slot="page-footer"]')) {
      Utils.consoleWarn(this.host, INVALID_FOOTER_SLOT);
      this._hasFooterSlot = false;
    }
  }

  /**
   * Validates that embedded In-Page Nav is not configured to display on
   * the same side of the page as embedded Sidebar(s), & swaps In-Page
   * Nav's position if it conflicts with Sidebar(s). Also sets default
   * position of In-Page Nav if "anchor" property is omitted from config.
   */
  initializeInPageNavConfig() {
    if (isNullOrUndefined(this.inPageNavConfig)) {
      this._inPageNavConfig = undefined;
      return;
    }

    const { anchor } = this.inPageNavConfig;
    this._inPageNavConfig = { ...this.inPageNavConfig };

    switch (true) {
      // If 2 Sidebars exist, do not render In-Page Nav
      case !!this.leftSidebarConfig && !!this.rightSidebarConfig:
        Utils.consoleWarn(this.host, IN_PAGE_NAV_INCOMPATIBLE_SIDEBARS);
        this._inPageNavConfig = undefined;
        break;
      // If Sidebar & In-Page Nav are anchored "left", swap In-Page Nav to "anchor: right"
      case !!this.leftSidebarConfig && anchor === FluidAnchorPosition.LEFT:
        Utils.consoleWarn(this.host, IN_PAGE_NAV_INVALID_ANCHOR(anchor));
        this._inPageNavConfig.anchor = FluidAnchorPosition.RIGHT;
        break;
      // If Sidebar & In-Page Nav are anchored "right", swap In-Page Nav to "anchor: left"
      case !!this.rightSidebarConfig && anchor === FluidAnchorPosition.RIGHT:
        Utils.consoleWarn(this.host, IN_PAGE_NAV_INVALID_ANCHOR(anchor));
        this._inPageNavConfig.anchor = FluidAnchorPosition.LEFT;
        break;
      // If no "anchor" is provided for In-Page Nav, default to "right" (or "left" if right-hand Sidebar exists)
      case isNullOrUndefined(anchor):
        this._inPageNavConfig.anchor = isNullOrUndefined(
          this.rightSidebarConfig
        )
          ? FluidAnchorPosition.RIGHT
          : FluidAnchorPosition.LEFT;
    }
  }

  /**
   * Maps individual deprecated hasSidebar, sidebarLinks, sidebarOpen
   * Props to Sidebar config object.
   */
  mapLocalToConfig(): void {
    if (this.hasSidebar === undefined && this.sidebarLinks === undefined)
      return;

    if (this.hasSidebar === false) {
      this.leftSidebarConfig = undefined;
    } else {
      this.leftSidebarConfig = {
        anchor: FluidAnchorPosition.LEFT,
        menuItems: this.sidebarLinks,
        visible: this.sidebarOpen,
      };
    }
  }

  /**
   * Subscribes to Element Registry BehaviorSubject, then watches for
   * Header & Footer components to be added to the registry. When either
   * of these components becomes available, adds their heights to
   * "_pageHeightOffset" & calculates FLUID Page's "min-height" so that
   * it is ALWAYS at least 100% of viewport height AND Footer is ALWAYS
   * at/below the bottom of the page (i.e. Footer doesn't "float" above
   * the fold). Also sets offset for Page Context Bar if Header is sticky.
   */
  subscribeToElementRegistry(): void {
    this._registrySubscription$ = ElementRegistry.registry$.subscribe(
      (registry: FluidKeyedObject<FluidElementState>) => {
        const headerHeight = registry[HEADER_ENTRY]?.domRect?.height ?? 0;
        const footerHeight = registry[FOOTER_ENTRY]?.domRect?.height ?? 0;

        if (registry[CONTEXT_BAR_ENTRY]) {
          this._contextBarOffset = registry[HEADER_ENTRY]?.dataStore?.sticky
            ? headerHeight
            : 0;
        } else {
          this._contextBarOffset = 0;
        }

        if (!headerHeight && !footerHeight) return;

        if (this._pageHeightOffset !== headerHeight + footerHeight) {
          this._pageHeightOffset = headerHeight + footerHeight;
        }
      }
    );
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div
        class={{
          'fluid-page': true,
          'atmospheric-bg': this.themeVariant === FluidThemeVariant.ATMOSPHERIC,
        }}
        style={
          !!this._pageHeightOffset && {
            minHeight: `calc(100vh - ${this._pageHeightOffset}px)`,
          }
        }>
        <PageSidebar
          config={this.leftSidebarConfig}
          anchor={FluidAnchorPosition.LEFT}
          theme={this.theme}
          themePalette={this.themePalette}
        />
        <PageNav
          config={this._inPageNavConfig}
          anchor={FluidAnchorPosition.LEFT}
          pageRoot={this.host}
          theme={this.theme}
        />
        <div class='main-content'>
          <PageContextBarSlot
            hasSlottedElement={this._hasContextBarSlot}
            offset={this._contextBarOffset}
          />
          <fluid-content
            themeVariant={this.themeVariant}
            mainInstance={true}
            theme={this.theme}
            overrideGlobalTheme={true}>
            {!!this.pageTitle && (
              <fluid-title level='1'>
                {this._i18nStore?.translate(this.fluidTranslate?.pageTitle) ||
                  this.pageTitle}
              </fluid-title>
            )}
            <slot />
          </fluid-content>
          <PageFooterSlot
            hasSlottedElement={this._hasFooterSlot}
            shadow={this.pageFooterDropShadow}
          />
        </div>
        <PageNav
          config={this._inPageNavConfig}
          anchor={FluidAnchorPosition.RIGHT}
          pageRoot={this.host}
          theme={this.theme}
        />
        <PageSidebar
          config={this.rightSidebarConfig}
          anchor={FluidAnchorPosition.RIGHT}
          theme={this.theme}
          themePalette={this.themePalette}
        />
      </div>
    );
  }

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "hasSidebar" is now deprecated. Instead, use
   * "leftSidebarConfig" or "rightSidebarConfig" Props. -- Renders left
   * Sidebar when true.
   * @deprecated
   */
  @Prop({ mutable: true }) hasSidebar: boolean;

  @Watch('hasSidebar')
  hasSidebarDeprecationWarning() {
    if (isNullOrUndefined(this.hasSidebar)) return;
    Utils.deprecationWarning(
      this.host,
      'Prop',
      'hasSidebar',
      'leftSidebarConfig',
      'FluidSidebarConfig'
    );
  }

  /**
   * DEPRECATED: "sidebarLinks" is now deprecated. Instead, use
   * "leftSidebarConfig" or "rightSidebarConfig" Prop with "menuItems"
   * property set. -- Array of FluidMenuItems to be rendered within
   * the left Sidebar.
   * @deprecated
   */
  @Prop({ mutable: true }) sidebarLinks: FluidMenuItem[];

  @Watch('sidebarLinks')
  sidebarLinksDeprecationWarning() {
    if (isNullOrUndefined(this.sidebarLinks)) return;
    Utils.deprecationWarning(
      this.host,
      'Prop',
      'sidebarLinks',
      'leftSidebarConfig.menuItems',
      'FluidSidebarConfig'
    );
  }

  /**
   * DEPRECATED: "sidebarOpen" is now deprecated. Instead, use
   * "leftSidebarConfig" or "rightSidebarConfig" Prop with "visible"
   * property set. -- Whether the left Sidebar should render as
   * open/expanded by default.
   * @deprecated
   */
  @Prop({ mutable: true }) sidebarOpen: boolean;

  @Watch('sidebarOpen')
  sidebarOpenDeprecationWarning() {
    if (isNullOrUndefined(this.sidebarOpen)) return;
    Utils.deprecationWarning(
      this.host,
      'Prop',
      'sidebarOpen',
      'leftSidebarConfig.visible',
      'FluidSidebarConfig'
    );
  }

  // ================================================================ //
  // -- Deprecated Events

  /**
   * DEPRECATED: The "sidebarLinkClicked" event is now deprecated.
   * Instead, listen for "menuItemClick" from the FLUID Tree Menu
   * component. -- Event emitted when a menu item within a Sidebar
   * Tree Menu is clicked.
   * @deprecated
   */
  @Event() sidebarLinkClicked: EventEmitter<FluidMenuItem>;
}
